/* eslint-disable spire/export-styles */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import { CartLineModel, CartModel, OrderLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Minus from "@insite/mobius/Icons/Minus";
import Plus from "@insite/mobius/Icons/Plus";
import Trash2 from "@NA/Icons/Trash2";
import React, { ChangeEvent, useEffect, useRef, useState, useMemo } from "react";
import { connect, ResolveThunks, useDispatch, useSelector } from "react-redux";
import TextField, { TextFieldProps } from "@insite/mobius/TextField";
import { css } from "styled-components";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import MiniCartModal from "@NA/Widgets/Cart/MiniCartModal";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import ADIColors from "@NA/Utils/ADIColors";
import { checkAllowedQuantity, isPriceAvailable } from "@NA/Utils/Utility";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import translate from "@insite/client-framework/Translate";
import Typography from "@insite/mobius/Typography";
import { removeProductFromCart } from "@NA/Overrides/Common/Utilities/CustomTracking";
import { addOrRemoveCart } from "@NA/Utils/GtmUtility";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import useDebounce from "@NA/Hooks/useDebounce";
import ADIMinimumQtyModal from "../ADIMinimumQtyModal";

interface OwnProps {
    productLine: ProductModel;
    buttonLabel: string;
    extendedStyles?: AdiProductAddToCartButtonStyles;
    sourceName: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: CustomApplicationState, props: OwnProps & HasProductContext) => {
    return {
        addToCartLoading: state.context.isUpdatingCart,
        cart: getCurrentCartState(state).value,
        productSettings: getSettingsCollection(state).productSettings,
        session: state.context.session,
        updateCartLoading: state.pages.cart.isUpdatingCartLine,
        removeCartLineLoading: state.pages.cart.isRemovingCartLine,
        isCartLoading: getCurrentCartState(state).isLoading || state.ADIGlobalData.ADIUpdateCartLoading.loading,
    };
};

export interface AdiProductAddToCartButtonStyles {
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    container?: InjectableCss;
    icons?: InjectableCss;
    field?: InjectableCss;
    loading?: InjectableCss;
    priceAvailibility?: InjectableCss;
    loadingInput?: InjectableCss;
}

export const adiProductAddToCartButtonStyles: AdiProductAddToCartButtonStyles = {
    addToCartButton: {
        css: css`
            width: 100%;
            text-align: center;
            padding: 20px 0;
            cursor: pointer;
            transition: 0.2s ease;
            margin-top: auto;
            border-radius: 0;
            background: ${ADIColors.white};
            height: 44px;
            display: flex;
            align-items: center;
            justify-content: center;
            :disabled {
                background: ${ADIColors.white} !important;
                cursor: not-allowed;
            }
            span {
                color: ${getColor("primary")};
            }
            :hover {
                background-color: ${getColor("primary")};
                span {
                    color: ${ADIColors.white};
                }
            }

            img {
                width: 40px;
            }
        `,
    },
    quantityOrdered: {
        cssOverrides: {
            formField: css`
                margin-right: 10px;
                width: 4.1rem;
                min-width: 4.1rem;
            `,
        },
    },
    container: {
        css: css`
            display: flex;
            width: 100%;
            border-top: 1px solid ${getColor("text.disabled")};
            margin-top: auto;
        `,
    },
    icons: {
        css: css`
            width: 35%;
            display: flex;
            justify-content: center;
            height: 64px;
            align-items: center;
            cursor: pointer;
            transition: all 0.2s ease;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            min-height: 55px;
                        `,
                    ],
                    "max",
                );
            }}
            svg {
                width: 20px;
                height: 20px;
                position: absolute;
                stroke-width: 2.3px;
            }

            :hover {
                background-color: ${getColor("primary")};
                svg {
                    filter: brightness(0) invert(1);
                }
            }
            &.minusIcon {
                :hover {
                    border-bottom-left-radius: 5px;
                }
                :active {
                    border-bottom-left-radius: 5px;
                }
            }
            &.plusIcon {
                :hover {
                    border-bottom-right-radius: 5px;
                }
                :active {
                    border-bottom-right-radius: 5px;
                }
            }
        `,
    },
    field: {
        css: css`
            width: 75%;
            [class^="FormFieldStyle-"] {
                height: 100%;
                width: 100%;
                display: block;
            }
            [class^="FormInputWrapper-"] {
                margin-top: 0px !important;
                height: 100%;

                input {
                    display: block;
                    width: 100%;
                    font-weight: 700;
                    border-radius: 0 !important;
                    border: 0;
                    text-align: center;
                    border: 1px solid ${getColor("text.disabled")};
                    height: 100%;
                    border-top: 0 !important;
                    border-bottom: 0 !important;
                }
            }
            input::-webkit-outer-spin-button,
            input::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            /* Firefox */
            input[type="number"] {
                -moz-appearance: textfield;
            }
        `,
    },
    loading: {
        css: css`
            width: 35%;
            display: flex;
            justify-content: center;
            position: relative;
            align-items: center;
            height: 64px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            min-height: 55px;
                        `,
                    ],
                    "max",
                );
            }}
            cursor: pointer;
            transition: all 0.2s ease;
            pointer-events: none;
            img {
                width: 40px;
                position: absolute;
            }
        `,
    },
    loadingInput: {
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            position: relative;
            align-items: center;
            height: 64px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        css`
                            min-height: 55px;
                        `,
                    ],
                    "max",
                );
            }}
            cursor: pointer;
            transition: all 0.2s ease;
            pointer-events: none;
            img {
                width: 40px;
                position: absolute;
            }
        `,
    },
    priceAvailibility: {
        css: css`
            font-weight: bold;
            font-size: ${getProp("theme.typography.headerSecondary.size")};
            padding: 0 15px 0px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme, [null, null, css` text-align: center;
                padding-bottom: 15px; `,], "max",);
            }}
            span {
                font-weight: bold;
                font-size: ${getProp("theme.typography.headerSecondary.size")};
            }
        `,
    },
};

const ADIProductAddToCartButton = ({
    productLine,
    addToCartLoading,
    buttonLabel,
    cart,
    extendedStyles,
    productSettings,
    productContext,
    sourceName,
    session,
    updateCartLoading,
    isCartLoading,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(adiProductAddToCartButtonStyles, extendedStyles));
    const [addToCartLoadingUpdated, setAddToCartLoadingUpdated] = useState(addToCartLoading);
    const [productQty, setProductQty] = useState("0");
    const [operationClicked, setOperationClicked] = useState("");
    const [product, setProduct] = useState<CartLineModel | null>();
    const [isMiniCartModalOpen, setIsMiniCartModalOpen] = useState(false);
    const [isMinimumQtyOpen, setIsMinimumQtyOpen] = useState(false);
    const [updateQuantityLoader, setUpdateQuantityLoader] = React.useState(false);

    const dispatch: any = useDispatch();
    const [addClicks, setAddClicks] = useState(0);
    const [minusClicks, setMinusClicks] = useState(0);
    const latestAddClicks = useRef(addClicks);
    const latestMinusClicks = useRef(minusClicks);
    const isPendingVerification = useSelector(
        (state: CustomApplicationState) => state.ADIGlobalData.PricingStatusStore.isPendingVerification,
    );
    const hasPriceLoaded = useMemo(() => {
        if (productContext && productContext.productInfo && productContext.productInfo.hasOwnProperty("pricing")) {
            return true;
        }

        return false;
    }, [productContext]);


    const openMiniCartHandler = () => setIsMiniCartModalOpen(true);

    const closeMinimumQtyCard = () => setIsMinimumQtyOpen(false);

    const closeMiniCartHandler = () => {
        setIsMiniCartModalOpen(false);
    };

    const { properties } = productLine;

    useEffect(() => {
        const product = cart && cart?.cartLines && cart.cartLines?.find(item => item.productId === productLine.id);
        product ? setProduct(product) : setProduct(null);
    }, [cart]);

    useEffect(() => {
        if (product && product.qtyOrdered) {
            setProductQty(product.qtyOrdered.toString());
        }
    }, [product]);

    const addToCartClickHandler = async () => {
        if (!addToCartLoading && !updateQuantityLoader) {
            sessionStorage.setItem("item_list_name", sourceName);
            setAddToCartLoadingUpdated(true);
            setOperationClicked("add");
            dispatch(
                addToCart({
                    productId: productLine.id,
                    qtyOrdered: parseInt(properties["minimuM_QTY"]) || 1,
                    unitOfMeasure: productLine.unitOfMeasureDisplay || "EA",
                    onComplete() {
                        setAddToCartLoadingUpdated(false);
                        setOperationClicked("");
                        if (productSettings.showAddToCartConfirmationDialog) {
                            openMiniCartHandler();
                        }
                    },
                }),
            );
        }
    };

    const debouncedIncreaseQuantity = useDebounce(() => {
        if (!addToCartLoading && product) {
            sessionStorage.setItem("item_list_name", sourceName);
            setAddToCartLoadingUpdated(true);
            setOperationClicked("increase");
            setAddClicks(0);
            dispatch(
                addToCart({
                    productId: product.productId as string,
                    qtyOrdered: latestAddClicks.current,
                    unitOfMeasure: product.unitOfMeasure,
                    onComplete() {
                        setAddToCartLoadingUpdated(false);
                        setOperationClicked("");
                        latestAddClicks.current = 0;
                    },
                }),
            );
        }
    }, 500);

    const debouncedDecreaseQuantity = useDebounce(() => {
        if (!addToCartLoading && product) {
            setAddToCartLoadingUpdated(true);
            setMinusClicks(0);
            dispatch(
                updateCartLine({
                    cartLine: {
                        ...(product as CartLineModel),
                        qtyOrdered: Number(Number(product.qtyOrdered) - latestMinusClicks.current),
                    },
                    onComplete() {
                        setAddToCartLoadingUpdated(false);
                        setMinusClicks(0);
                        latestMinusClicks.current = 0;
                    },
                }),
            );
        }
    }, 500);

    const increaseQuantity = async () => {
        if (!addToCartLoading && product && !isCartLoading) {
            setAddClicks(prevClicks => prevClicks + Number(product.properties.minimuM_QTY));
            latestAddClicks.current = addClicks + Number(product.properties.minimuM_QTY);
            // if (!checkAllowedQuantity(Number(productQty), Number(product.properties.minimuM_QTY))) {
            //     setIsMinimumQtyOpen(true);
            //     return;
            // }
            setProductQty(prev => String(Number(prev) + parseInt(product.properties.minimuM_QTY)));
            sessionStorage.setItem("item_list_name", sourceName);
            debouncedIncreaseQuantity(product.id);
        }
    };

    const decreaseQuantity = () => {
        if (!product) return;
        if (!addToCartLoading && product) {
            setOperationClicked("decrease");

            // if (!checkAllowedQuantity(Number(productQty), Number(product.properties.minimuM_QTY))) {
            //     setIsMinimumQtyOpen(true);
            //     return;
            // }

            if (productQty === product.properties.minimuM_QTY) {
                setAddToCartLoadingUpdated(true);
                dispatch(
                    removeCartLine({
                        cartLineId: product.id,
                        onComplete() {
                            setAddToCartLoadingUpdated(false);
                            setOperationClicked("");
                        },
                    }),
                );
                if (product) {
                    sessionStorage.setItem("item_list_name", sourceName);
                    removeProductFromCart(product, "Buy again from order details page", session);
                }
            } else {
                setMinusClicks(prevClicks => prevClicks + Number(product.properties.minimuM_QTY));
                latestMinusClicks.current = minusClicks + Number(product.properties.minimuM_QTY);
                setProductQty(prev => String(Number(prev) - parseInt(product.properties.minimuM_QTY)));
                debouncedDecreaseQuantity();
                if (product) {
                    sessionStorage.setItem("item_list_name", sourceName);
                    const cart: CartLineModel = product;
                    removeProductFromCart(cart, "Order Details", session);
                }
            }
        }
    };

    const updateQuantity = (event: React.ChangeEvent<HTMLInputElement>) => {
        setAddToCartLoadingUpdated(true);
        setOperationClicked("update");
        const value = event.target.value;
        if (!product) return;

        if (value === "" || value === product?.qtyOrdered!.toString()) {
            setAddToCartLoadingUpdated(false);
            setProductQty(product?.qtyOrdered!.toString());
        }

        if (!value && value !== "0") {
            return;
        }
        if (value === "0") {
            setUpdateQuantityLoader(true);
            dispatch(
                removeCartLine({
                    cartLineId: product.id,
                    onComplete() {
                        setAddToCartLoadingUpdated(false);
                        setUpdateQuantityLoader(false);
                        setOperationClicked("");
                    },
                }),
            );

            sessionStorage.setItem("item_list_name", sourceName);
            addOrRemoveCart(product, 0, session);
            return;
        }
        if (Boolean(value) && Number(value) && product.qtyOrdered !== Number(value)) {
            if (!checkAllowedQuantity(Number(value), Number(properties.minimuM_QTY))) {
                if (product && product.qtyOrdered) {
                    setProductQty(product.qtyOrdered?.toString());
                }
                setIsMinimumQtyOpen(true);
                return;
            }

            setUpdateQuantityLoader(true);
            dispatch(
                updateCartLine({
                    cartLine: {
                        ...product,
                        qtyOrdered: Number(event.target.value),
                    },
                    onComplete() {
                        setAddToCartLoadingUpdated(false);
                        setUpdateQuantityLoader(false);
                        setOperationClicked("");
                    },
                }),
            );
            sessionStorage.setItem("item_list_name", sourceName);
            const qty = Number(event.target.value);
            addOrRemoveCart(product, qty, session);
        }
    };

    const qtyChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setProductQty(value);
    };

    const addLoader = (type = "counter") => {
        return (
            <StyledWrapper {...(type === "counter" ? styles.loading : styles.loadingInput)}>
                <img alt="" src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/rd-addtocart-loader.gif" />
            </StyledWrapper>
        );
    };

    const pricingIsAvailable = isPriceAvailable(
        productContext.productInfo?.pricing,
        productContext.productInfo?.qtyOrdered,
    );

    if (hasPriceLoaded && !pricingIsAvailable && productContext.productInfo && !isPendingVerification) {
        return (
            <StyledWrapper {...adiProductAddToCartButtonStyles.priceAvailibility}>
                <StyledWrapper>
                    {localStorage.getItem("CreditStatus") !== "O" && localStorage.getItem("CreditStatus") !== "" && localStorage.getItem("CreditStatus") !== null ? <Typography>{translate("Account Pending Verification")}</Typography> : <Typography>{translate("Pricing Currently Not Available")}</Typography>}
                </StyledWrapper>
            </StyledWrapper>
        );
    }
    return (
        <>
            {product && !isPendingVerification && (
                <StyledWrapper
                    css={css`
                        display: flex;
                    `}
                    {...styles.container}
                >
                    <StyledWrapper
                        onClick={() => {
                            if (!addToCartLoading && !updateCartLoading && !isCartLoading) {
                                decreaseQuantity();
                            }
                        }}
                        {...styles.icons}
                        className="minusIcon"
                    >
                        {addToCartLoadingUpdated && operationClicked === "decrease" ? (
                            addLoader()
                        ) : productQty === "1" || productQty === "0" ? (
                            <Trash2 />
                        ) : (
                            <Minus />
                        )}
                    </StyledWrapper>

                    <StyledWrapper {...styles.field}>
                        {updateQuantityLoader && operationClicked === "update" ? (
                            addLoader("input")
                        ) : (
                            <TextField
                                value={productQty}
                                type="text"
                                min={0}
                                max={999999}
                                maxLength={5}
                                disabled={addToCartLoadingUpdated || addToCartLoading}
                                data-test-selector="product_qtyOrdered"
                                onBlur={updateQuantity}
                                onChange={qtyChangeHandler}
                            />
                        )}
                    </StyledWrapper>
                    <StyledWrapper
                        onClick={() => {
                            if (!addToCartLoading && !updateCartLoading && !isCartLoading) {
                                increaseQuantity();
                            }
                        }}
                        {...styles.icons}
                        className="plusIcon"
                    >
                        {addToCartLoadingUpdated && operationClicked === "increase" ? addLoader() : <Plus />}
                    </StyledWrapper>
                </StyledWrapper>
            )}
            {!product && !isPendingVerification && (
                <Button
                    {...styles.addToCartButton}
                    onClick={addToCartClickHandler}
                    disabled={addToCartLoading || updateCartLoading || isCartLoading}
                    variant="secondary"
                >
                    {addToCartLoadingUpdated ? (
                        <img alt="" src="https://cdn.adiglobaldistribution.us/userfile-na/US/userfiles/Images/Themes/icons/rd-addtocart-loader.gif" />
                    ) : (
                        buttonLabel
                    )}
                </Button>
            )}
            {product && !isPendingVerification && (
                <MiniCartModal
                    isModalOpen={isMiniCartModalOpen}
                    onModalClose={closeMiniCartHandler}
                    cartLine={product}
                    quantityOrdered={Number(product.qtyOrdered)}
                />
            )}
            <ADIMinimumQtyModal
                headline="Add to Cart Message"
                isOpen={isMinimumQtyOpen}
                errorStyle={false}
                handleClose={closeMinimumQtyCard}
            >
                <>
                    {translate("Must be purchased in quantities of")} {product && product.properties.minimuM_QTY}
                </>
            </ADIMinimumQtyModal>
        </>
    );
};
export default withProductContext(connect(mapStateToProps)(ADIProductAddToCartButton));
